// Iter38/39 — Sorting Results section embedded in the incident detail panel.
//
// Lists all sorting_results for an incident as compact cards, with the Defect
// Pareto chart below. Cards expose:
//   - INC/SORT IDs, defect rate (color-coded), effectiveness
//   - OK/NOK/Rework qty breakdown
//   - Defect type chips
//   - Location, sorter count, supervisor
//   - Submitted-by + timestamp
//   - PDF download button per card (iter39)
//
// Section toolbar (iter39): Excel export + Excel import (with dry-run preview).
//
// "Submit Sorting Result" button surfaces SortingResultForm — only available
// when there's a sorting-type service request on the incident.
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useT } from "../lib/i18n";
import { DefectPareto } from "./DefectPareto";
import { SortingResultForm } from "./SortingResultForm";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rateColor = (rate) => (rate > 10 ? "#E24B4A" : rate > 5 ? "#f3b14a" : "#4ed084");

const SortingCard = ({ r, t }) => {
  const ok = r.total_ok ?? r.ok_qty ?? 0;
  const nok = r.total_nok ?? r.nok_qty ?? 0;
  const rew = r.total_rework ?? r.rework_qty ?? 0;
  const total = ok + nok + rew;
  const rate = r.overall_defect_rate_pct ?? r.defect_rate_pct ?? 0;
  const entriesCount = (r.entries || []).length;
  const rc = rateColor(rate);
  const downloadPdf = async () => {
    try {
      const resp = await axios.get(`${API}/sorting-results/${r.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.id}-summary.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };
  return (
    <div
      className="rounded-md p-3 mb-2"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: `1px solid rgba(255,255,255,0.10)`,
        borderLeft: `3px solid ${rc}`,
      }}
      data-testid={`sorting-result-card-${r.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec", letterSpacing: "0.6px" }}>{r.id}</span>
          {r.finalized && (
            <span style={{ background: "rgba(120,125,140,0.16)", color: "#9aa1b3", padding: "1px 5px", borderRadius: 2, fontFamily: "DM Mono, monospace", fontSize: 8, letterSpacing: "0.5px" }}>
              FINAL
            </span>
          )}
          {entriesCount > 1 && (
            <span style={{ background: "rgba(155,89,182,0.16)", color: "#c89adb", padding: "1px 5px", borderRadius: 2, fontFamily: "DM Mono, monospace", fontSize: 8, letterSpacing: "0.5px" }}>
              {entriesCount} ENTRIES
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 16, fontWeight: 700, color: rc }} data-testid={`sorting-result-rate-${r.id}`}>
            {rate.toFixed(1)}%
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-2">
        <QtyChip label="OK" value={ok} color="#4ed084" testId={`sorting-result-ok-${r.id}`} />
        <QtyChip label="NOK" value={nok} color="#E24B4A" testId={`sorting-result-nok-${r.id}`} />
        <QtyChip label="REW" value={rew} color="#f3b14a" testId={`sorting-result-rework-${r.id}`} />
      </div>

      {r.defect_types && r.defect_types.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`sorting-result-defects-${r.id}`}>
          {r.defect_types.map((d) => (
            <span
              key={d}
              style={{
                background: "rgba(226,75,74,0.10)",
                color: "#ff9a99",
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                padding: "1px 5px",
                borderRadius: 2,
                letterSpacing: "0.3px",
              }}
            >
              {d}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3" style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.3px" }}>
        <span>
          {t("sorting.card.total")}: <span style={{ color: "var(--text, #f0f2f8)" }}>{total.toLocaleString()}</span>
        </span>
        <span>
          {t("sorting.card.location")}: <span style={{ color: "var(--text, #f0f2f8)" }}>{r.sorting_location}</span>
        </span>
        <span>
          {t("sorting.card.sorters")}: <span style={{ color: "var(--text, #f0f2f8)" }}>{r.sorter_count}</span>
        </span>
        {r.supervisor_name && (
          <span>
            {t("sorting.card.supervisor")}: <span style={{ color: "var(--text, #f0f2f8)" }}>{r.supervisor_name}</span>
          </span>
        )}
        <span>
          {new Date(r.created_at).toLocaleString()}
        </span>
        <button
          type="button"
          onClick={downloadPdf}
          style={{
            marginLeft: "auto",
            padding: "2px 8px",
            borderRadius: 3,
            background: "rgba(24,95,165,0.12)",
            border: "1px solid rgba(24,95,165,0.40)",
            color: "#9ec3ec",
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            letterSpacing: "0.5px",
            cursor: "pointer",
          }}
          data-testid={`sorting-result-pdf-${r.id}`}
        >
          📥 {t("sorting.card.pdf") === "sorting.card.pdf" ? "Download PDF" : t("sorting.card.pdf")}
        </button>
      </div>
    </div>
  );
};

const QtyChip = ({ label, value, color, testId }) => (
  <div
    className="rounded p-1.5 text-center"
    style={{ background: `${color}10`, border: `1px solid ${color}40` }}
    data-testid={testId}
  >
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 8, color, letterSpacing: "0.8px" }}>{label}</div>
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 14, fontWeight: 700, color: "var(--text, #f0f2f8)" }}>
      {(value || 0).toLocaleString()}
    </div>
  </div>
);

export const SortingResultsSection = ({ incidentId, serviceRequests = [] }) => {
  const { t } = useT();
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpenSrId, setFormOpenSrId] = useState(null);
  const [importingFor, setImportingFor] = useState(null); // sr_id of import preview
  const [importPreview, setImportPreview] = useState(null); // dry-run response
  const [importBusy, setImportBusy] = useState(false);
  const importInputRef = useRef(null);
  const importTargetSrRef = useRef(null);

  const sortingSrs = useMemo(
    () => serviceRequests.filter((sr) => sr.type === "sorting" && sr.status !== "cancelled"),
    [serviceRequests],
  );

  const canSubmit = hasModuleAccess(user, "sorting") && ["responder", "escalation_manager", "tenant_admin", "platform_admin"].includes(user?.role);

  const refresh = async () => {
    if (!incidentId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/incidents/${incidentId}/sorting-results`);
      setResults(Array.isArray(data) ? data : []);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [incidentId]);

  const onSubmitted = () => {
    setFormOpenSrId(null);
    refresh();
  };

  const exportExcel = async () => {
    try {
      const resp = await axios.get(`${API}/incidents/${incidentId}/sorting-results.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${incidentId}-sorting-results.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(`${API}/sorting-results/import-template.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sitroom-sorting-import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };

  const openImportDialog = (srId) => {
    importTargetSrRef.current = srId;
    importInputRef.current?.click();
  };

  const onImportFile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file || !importTargetSrRef.current) return;
    const srId = importTargetSrRef.current;
    setImportingFor(srId);
    setImportBusy(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/service-requests/${srId}/sorting-results/import?dry_run=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportPreview({ ...data, _file: file, _sr_id: srId });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Import failed";
      addToast({ type: "error", title: "Import failed", body: typeof msg === "string" ? msg : "Could not parse file" });
      setImportingFor(null);
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    if (!importPreview || !importPreview._file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview._file);
      const { data } = await axios.post(
        `${API}/service-requests/${importPreview._sr_id}/sorting-results/import?dry_run=false`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      addToast({
        type: "success",
        title: "Sorting results imported",
        body: `${(data.created_ids || []).length} result(s) created`,
      });
      setImportPreview(null);
      setImportingFor(null);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Import failed";
      addToast({ type: "error", title: "Import failed", body: typeof msg === "string" ? msg : "Server error" });
    } finally {
      setImportBusy(false);
    }
  };

  const cancelImport = () => {
    setImportPreview(null);
    setImportingFor(null);
  };

  // No sorting SRs and no results — render a quiet placeholder so the section
  // doesn't clutter the panel.
  if (sortingSrs.length === 0 && results.length === 0) return null;

  return (
    <div className="mt-4" data-testid="sorting-results-section">
      <div
        className="flex items-center justify-between mb-2"
        style={{ fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "1.4px", color: "#9198b0", textTransform: "uppercase" }}
      >
        <span>{t("sorting.section_title")}</span>
        <div className="flex items-center gap-2">
          {results.length > 0 && (
            <button
              type="button"
              onClick={exportExcel}
              style={{
                padding: "2px 8px",
                borderRadius: 3,
                background: "rgba(78,208,132,0.10)",
                border: "1px solid rgba(78,208,132,0.30)",
                color: "#9aebc2",
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                letterSpacing: "0.5px",
                cursor: "pointer",
              }}
              data-testid="sorting-export-xlsx-btn"
            >
              📊 XLSX
            </button>
          )}
          <span data-testid="sorting-results-count">{results.length}</span>
        </div>
      </div>

      {/* hidden file input for Excel import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={onImportFile}
        style={{ display: "none" }}
        data-testid="sorting-import-file-input"
      />

      {canSubmit && sortingSrs.map((sr) =>
        formOpenSrId === sr.id ? (
          <div key={sr.id} className="mb-3" data-testid={`sorting-form-wrapper-${sr.id}`}>
            <SortingResultForm
              srId={sr.id}
              incidentId={incidentId}
              onSubmitted={onSubmitted}
              onCancel={() => setFormOpenSrId(null)}
            />
          </div>
        ) : (
          <div key={sr.id} className="mb-2 flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => setFormOpenSrId(sr.id)}
              className="flex-1 px-3 py-2 rounded-md flex items-center justify-between transition-colors"
              style={{
                background: "rgba(24,95,165,0.08)",
                border: "1px solid rgba(24,95,165,0.30)",
                color: "#9ec3ec",
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                letterSpacing: "0.5px",
                cursor: "pointer",
              }}
              data-testid={`sorting-submit-btn-${sr.id}`}
            >
              <span>{t("sorting.submit_for")} · {sr.id}</span>
              <span style={{ fontSize: 14 }}>＋</span>
            </button>
            <button
              type="button"
              onClick={() => openImportDialog(sr.id)}
              className="px-3 py-2 rounded-md"
              style={{
                background: "rgba(155,89,182,0.08)",
                border: "1px solid rgba(155,89,182,0.30)",
                color: "#c89adb",
                fontFamily: "DM Mono, monospace",
                fontSize: 10,
                letterSpacing: "0.5px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Import .xlsx"
              data-testid={`sorting-import-btn-${sr.id}`}
            >
              📥 Import
            </button>
          </div>
        ),
      )}

      {/* Template download hint */}
      {canSubmit && sortingSrs.length > 0 && formOpenSrId === null && (
        <button
          type="button"
          onClick={downloadTemplate}
          className="mb-2"
          style={{
            background: "transparent",
            border: "none",
            color: "#6b7388",
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            letterSpacing: "0.4px",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          data-testid="sorting-template-link"
        >
          ↓ download .xlsx import template
        </button>
      )}

      {/* Import preview modal */}
      {importPreview && (
        <div
          className="mb-3 rounded-md p-3"
          style={{
            background: "var(--bg-surface, #0d0f18)",
            border: "1px solid rgba(155,89,182,0.40)",
          }}
          data-testid="sorting-import-preview"
        >
          <div className="flex items-center justify-between mb-2">
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text, #f0f2f8)" }}>
              Import preview — {importPreview.would_create} row(s)
            </span>
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0" }}>{importingFor}</span>
          </div>
          {(importPreview.errors || []).length > 0 && (
            <div
              className="mb-2 p-2 rounded"
              style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)" }}
              data-testid="sorting-import-errors"
            >
              <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#ff9a99", marginBottom: 4 }}>
                {importPreview.errors.length} error(s) — fix and re-upload
              </div>
              <ul style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#ff9a99", paddingLeft: 16 }}>
                {importPreview.errors.slice(0, 8).map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            </div>
          )}
          {(importPreview.preview || []).length > 0 && (
            <div className="mb-2" style={{ maxHeight: 180, overflowY: "auto" }} data-testid="sorting-import-rows">
              {importPreview.preview.slice(0, 20).map((p, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1" style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#c5cad8", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <span>{p.ok_qty}/{p.nok_qty}/{p.rework_qty} · {p.location}</span>
                  <span style={{ color: rateColor(p.defect_rate_pct) }}>{p.defect_rate_pct}%</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cancelImport}
              className="px-3 py-1.5 rounded"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11, cursor: "pointer" }}
              data-testid="sorting-import-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmImport}
              disabled={importBusy || (importPreview.errors || []).length > 0}
              className="px-3 py-1.5 rounded"
              style={{
                background: (importPreview.errors || []).length > 0 ? "#3a3f55" : "#185FA5",
                color: "#fff",
                fontFamily: "Syne, sans-serif",
                fontWeight: 700,
                fontSize: 11,
                cursor: importBusy ? "wait" : (importPreview.errors || []).length > 0 ? "not-allowed" : "pointer",
                opacity: importBusy ? 0.6 : 1,
              }}
              data-testid="sorting-import-confirm-btn"
            >
              {importBusy ? "Importing…" : `Confirm import (${importPreview.would_create})`}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-4" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#6b7388" }} data-testid="sorting-results-loading">
          {t("sorting.loading")}
        </div>
      )}

      {results.map((r) => <SortingCard key={r.id} r={r} t={t} />)}

      {results.length > 1 && <DefectPareto results={results} testId="sorting-section-pareto" />}
    </div>
  );
};
